import {useState} from 'react';
import type {FormDataEntryProgressStep} from '../components/Container/Presenter/Progress/ProgressStepDrawer';
import {validateProgressStepForm} from './validateProgressStepForm';

type FormState = {
  data: FormDataEntryProgressStep;
  errors: Partial<Record<keyof FormDataEntryProgressStep, string>>;
  isSubmitting: boolean;
};

export const useProgressStepForm = (
  initialData?: Partial<FormDataEntryProgressStep>,
) => {
  const [formState, setFormState] = useState<FormState>({
    data: {
      stepOrder: initialData?.stepOrder ?? 1,
      stepName: initialData?.stepName ?? '',
      deadline: initialData?.deadline, // undefined で初期化（tri-state対応）
      notes: initialData?.notes, // undefined で初期化（tri-state対応）
      result: initialData?.result,
      startDate: initialData?.startDate,
      endDate: initialData?.endDate,
      place: initialData?.place,
      stepId: initialData?.stepId,
    },
    errors: {},
    isSubmitting: false,
  });

  const updateField = (
    field: keyof FormDataEntryProgressStep,
    value: string | number | undefined,
  ) => {
    setFormState((prev) => ({
      ...prev,
      data: {...prev.data, [field]: value},
      // フィールド更新時にエラーをクリア
      errors: {...prev.errors, [field]: undefined},
    }));
  };

  const validate = () => {
    const {isValid, errors} = validateProgressStepForm(formState.data);
    setFormState((prev) => ({...prev, errors}));
    return isValid;
  };

  const validateField = (field: keyof FormDataEntryProgressStep) => {
    const {errors} = validateProgressStepForm(formState.data);
    setFormState((prev) => ({
      ...prev,
      errors: {
        ...prev.errors,
        [field]: errors[field],
      },
    }));
  };

  const reset = (overrides?: Partial<FormDataEntryProgressStep>) => {
    setFormState({
      data: {
        stepOrder: overrides?.stepOrder ?? 1,
        stepName: overrides?.stepName ?? '',
        deadline: overrides?.deadline, // undefined で初期化（tri-state対応）
        notes: overrides?.notes, // undefined で初期化（tri-state対応）
        result: overrides?.result,
        startDate: overrides?.startDate,
        endDate: overrides?.endDate,
        place: overrides?.place,
        stepId: overrides?.stepId,
      },
      errors: {},
      isSubmitting: false,
    });
  };

  const setIsSubmitting = (isSubmitting: boolean) => {
    setFormState((prev) => ({...prev, isSubmitting}));
  };

  return {
    formState,
    updateField,
    validate,
    validateField,
    reset,
    setIsSubmitting,
  };
};
